import React, { useCallback, useEffect, useState } from 'react';
import {
  Plus, Save, Trash2, ChevronUp, ChevronDown, X, Loader2, Edit3,
  ToggleLeft, ToggleRight, Mail, Copy, Power,
} from 'lucide-react';
import api from '../api/client';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';

/**
 * AdminFormBuilder — Phase 48C.
 * -----------------------------------------------------------------
 * CMS-like editor for help-center forms. No code changes required:
 * every property (title, description, target_email, fields[]) is
 * editable inline. Field types mirror the public renderer so a form
 * saved here always renders faithfully in the user-facing modal.
 *
 * Data flow:
 *   • On mount → GET /admin/help/forms (returns all, incl. inactive).
 *   • Editing a form opens a right-hand panel with a full form editor.
 *   • Save → PUT /admin/help/forms/{id} (or POST for new).
 *   • Delete → soft delete via DELETE (server flips is_active=false).
 *
 * Design constraint: we deliberately keep the editor a single form
 * (no nested modals) so admins can review everything at a glance.
 */

const FIELD_TYPES = [
  { value: 'text',      label: 'Testo breve' },
  { value: 'textarea',  label: 'Testo lungo' },
  { value: 'email',     label: 'Email' },
  { value: 'phone',     label: 'Telefono' },
  { value: 'number',    label: 'Numero' },
  { value: 'date',      label: 'Data' },
  { value: 'select',    label: 'Menu a tendina' },
  { value: 'radio',     label: 'Scelta multipla' },
  { value: 'checkbox',  label: 'Checkbox' },
  { value: 'file',      label: 'Upload allegato' },
];

const emptyLocalized = () => ({ it: '', en: '' });

const emptyField = () => ({
  key: `field_${Math.random().toString(36).slice(2, 8)}`,
  type: 'text',
  label: emptyLocalized(),
  placeholder: emptyLocalized(),
  help_text: emptyLocalized(),
  required: false,
  options: [],
  validation: {},
});

const emptyForm = () => ({
  key: '',
  category: '',
  order: 100,
  title: emptyLocalized(),
  description: emptyLocalized(),
  target_email: 'support@nymia.me',
  is_active: true,
  fields: [emptyField()],
});

const AdminFormBuilder = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // form object being edited, or null
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/help/forms');
      setForms(res.data.forms || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setEditing(emptyForm());
    setIsNew(true);
  };

  const startEdit = (form) => {
    // Deep clone so edits don't mutate the list until save.
    setEditing(JSON.parse(JSON.stringify(form)));
    setIsNew(false);
  };

  const cancel = () => {
    setEditing(null);
    setIsNew(false);
  };

  const save = async () => {
    if (!editing) return;
    // Client-side sanity checks — server also validates.
    if (!editing.key?.trim()) return toast.error('Chiave modulo obbligatoria');
    if (!editing.title?.it?.trim() && !editing.title?.en?.trim()) {
      return toast.error('Titolo IT o EN obbligatorio');
    }
    if (!editing.target_email?.trim()) {
      return toast.error('Email di destinazione obbligatoria');
    }
    if (!editing.fields?.length) return toast.error('Aggiungi almeno un campo');
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/admin/help/forms', editing);
        toast.success('Modulo creato');
      } else {
        await api.put(`/admin/help/forms/${editing.id}`, editing);
        toast.success('Modulo aggiornato');
      }
      await load();
      cancel();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Errore di salvataggio');
    } finally { setSaving(false); }
  };

  const softDelete = async (form) => {
    if (!window.confirm(`Disattivare il modulo "${form.key}"? Potrai riattivarlo dall'editor.`)) return;
    try {
      await api.delete(`/admin/help/forms/${form.id}`);
      toast.success('Modulo disattivato');
      await load();
    } catch (_e) {
      toast.error('Errore di disattivazione');
    }
  };

  // Phase 52 · Toggle active state without opening the editor.
  const toggleActive = async (form) => {
    try {
      await api.put(`/admin/help/forms/${form.id}`, {
        ...form,
        is_active: !form.is_active,
      });
      toast.success(form.is_active ? 'Modulo disattivato' : 'Modulo attivato');
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Errore aggiornamento');
    }
  };

  // Phase 52 · Duplicate a form via the dedicated endpoint. The clone is
  // returned inactive so admins can polish it before publishing.
  const duplicate = async (form) => {
    try {
      const res = await api.post(`/admin/help/forms/${form.id}/duplicate`);
      toast.success('Modulo duplicato');
      await load();
      // Open the fresh copy so admins can immediately rename it.
      if (res.data?.id) startEdit(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Errore di duplicazione');
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[360px_1fr]" data-testid="admin-form-builder">
      {/* List */}
      <div className="rounded-xl border border-border bg-card/40">
        <div className="flex items-center justify-between border-b border-border p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Moduli</p>
          <Button size="sm" onClick={startNew} className="gold-gradient text-black" data-testid="admin-form-builder-new">
            <Plus className="mr-1 h-3 w-3" /> Nuovo
          </Button>
        </div>
        <div className="max-h-[600px] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-4 w-4 animate-spin" /></div>
          ) : forms.length === 0 ? (
            <p className="p-4 text-xs text-muted-foreground">Nessun modulo. Crea il primo.</p>
          ) : (
            forms.map((f) => (
              <div
                key={f.id}
                className={`border-b border-border/50 p-3 transition ${
                  editing?.id === f.id ? 'bg-amber-500/10' : 'hover:bg-white/[0.03]'
                }`}
                data-testid={`admin-form-builder-row-${f.key}`}
              >
                <div className="flex items-start gap-2">
                  <button
                    type="button"
                    onClick={() => startEdit(f)}
                    className="min-w-0 flex-1 text-left"
                    data-testid={`admin-form-builder-open-${f.key}`}
                  >
                    <div className="flex items-center gap-2">
                      <p className="truncate text-[13px] font-semibold">{f.title?.it || f.title?.en || f.key}</p>
                      {!f.is_active && (
                        <span className="rounded bg-white/10 px-1 py-[1px] text-[9px] uppercase tracking-wide text-muted-foreground">
                          off
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">{f.key}</p>
                    <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-muted-foreground">
                      <Mail className="h-2.5 w-2.5" /> {f.target_email}
                    </p>
                  </button>
                  <span className="mt-0.5 shrink-0 rounded bg-white/[0.03] px-1.5 py-[1px] text-[10px] text-muted-foreground">
                    {f.fields?.length || 0} campi
                  </span>
                </div>
                {/* Phase 52 · Explicit inline actions — no need to open the editor */}
                <div className="mt-2 flex flex-wrap items-center gap-1">
                  <button
                    type="button"
                    onClick={() => startEdit(f)}
                    className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] text-muted-foreground hover:bg-white/10 hover:text-foreground"
                    data-testid={`admin-form-builder-edit-${f.key}`}
                  >
                    <Edit3 className="h-3 w-3" /> Modifica
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleActive(f)}
                    className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] transition-colors ${
                      f.is_active
                        ? 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10'
                        : 'border-white/20 text-muted-foreground hover:bg-white/10'
                    }`}
                    data-testid={`admin-form-builder-toggle-${f.key}`}
                  >
                    <Power className="h-3 w-3" />
                    {f.is_active ? 'Disattiva' : 'Attiva'}
                  </button>
                  <button
                    type="button"
                    onClick={() => duplicate(f)}
                    className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] text-muted-foreground hover:bg-white/10 hover:text-foreground"
                    data-testid={`admin-form-builder-duplicate-${f.key}`}
                  >
                    <Copy className="h-3 w-3" /> Duplica
                  </button>
                  <button
                    type="button"
                    onClick={() => softDelete(f)}
                    className="inline-flex items-center gap-1 rounded-md border border-red-500/30 px-2 py-1 text-[11px] text-red-300 hover:bg-red-500/10"
                    data-testid={`admin-form-builder-archive-${f.key}`}
                  >
                    <Trash2 className="h-3 w-3" /> Archivia
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="rounded-xl border border-border bg-card/40 lg:min-h-[600px]">
        {editing ? (
          <FormEditor
            form={editing}
            onChange={setEditing}
            onSave={save}
            onCancel={cancel}
            onDelete={!isNew ? () => softDelete(editing) : null}
            saving={saving}
          />
        ) : (
          <div className="flex h-full min-h-[300px] items-center justify-center px-6 text-center text-xs text-muted-foreground">
            Seleziona un modulo dalla lista per modificarlo, oppure crea un nuovo modulo.
          </div>
        )}
      </div>
    </div>
  );
};

// ==================================================================
// Editor
// ==================================================================

const FormEditor = ({ form, onChange, onSave, onCancel, onDelete, saving }) => {
  const patch = (delta) => onChange({ ...form, ...delta });
  const patchLocalized = (key, lang, value) => {
    onChange({ ...form, [key]: { ...(form[key] || {}), [lang]: value } });
  };

  const setField = (idx, next) => {
    const fields = [...form.fields];
    fields[idx] = next;
    onChange({ ...form, fields });
  };
  const addField = () => onChange({ ...form, fields: [...(form.fields || []), emptyField()] });
  const removeField = (idx) => {
    const fields = form.fields.filter((_, i) => i !== idx);
    onChange({ ...form, fields });
  };
  const moveField = (idx, dir) => {
    const next = dir === 'up' ? idx - 1 : idx + 1;
    if (next < 0 || next >= form.fields.length) return;
    const fields = [...form.fields];
    [fields[idx], fields[next]] = [fields[next], fields[idx]];
    onChange({ ...form, fields });
  };

  return (
    <div className="flex h-full flex-col" data-testid="admin-form-builder-editor">
      {/* Editor header */}
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-card/95 p-3 backdrop-blur">
        <div className="flex items-center gap-2">
          <Edit3 className="h-4 w-4 text-amber-300" />
          <p className="text-sm font-semibold">Editor modulo</p>
          <button
            type="button"
            onClick={() => patch({ is_active: !form.is_active })}
            className="ml-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            data-testid="admin-form-builder-toggle-active"
          >
            {form.is_active
              ? <ToggleRight className="h-4 w-4 text-emerald-400" />
              : <ToggleLeft className="h-4 w-4 text-muted-foreground" />}
            {form.is_active ? 'Attivo' : 'Disattivato'}
          </button>
        </div>
        <div className="flex items-center gap-2">
          {onDelete && (
            <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-300 hover:text-red-200" data-testid="admin-form-builder-delete">
              <Trash2 className="mr-1 h-3 w-3" /> Disattiva
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onCancel}>Annulla</Button>
          <Button size="sm" onClick={onSave} disabled={saving} className="gold-gradient text-black" data-testid="admin-form-builder-save">
            {saving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}
            Salva
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Meta */}
        <section className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-[12px]">Chiave modulo (form_key)</Label>
              <Input
                value={form.key}
                onChange={(e) => patch({ key: e.target.value.replace(/[^a-z0-9_]/gi, '').toLowerCase() })}
                placeholder="es. creators"
                className="mt-1 font-mono"
                data-testid="admin-form-builder-key"
              />
            </div>
            <div>
              <Label className="text-[12px]">Categoria</Label>
              <Input value={form.category} onChange={(e) => patch({ category: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-[12px]">Ordine</Label>
              <Input type="number" value={form.order} onChange={(e) => patch({ order: Number(e.target.value) || 0 })} className="mt-1" />
            </div>
            <div>
              <Label className="text-[12px] flex items-center gap-1">
                <Mail className="h-3 w-3" /> Email di destinazione
              </Label>
              <Input
                type="email"
                value={form.target_email || ''}
                onChange={(e) => patch({ target_email: e.target.value })}
                placeholder="support@nymia.me"
                className="mt-1"
                data-testid="admin-form-builder-target-email"
              />
              <p className="mt-1 text-[10.5px] text-muted-foreground">
                Le richieste inviate con questo modulo vengono associate a questa email (visibile nel dettaglio ticket).
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <LocalizedInput label="Titolo (IT)" value={form.title?.it || ''} onChange={(v) => patchLocalized('title', 'it', v)} testId="admin-form-builder-title-it" />
            <LocalizedInput label="Titolo (EN)" value={form.title?.en || ''} onChange={(v) => patchLocalized('title', 'en', v)} testId="admin-form-builder-title-en" />
            <LocalizedTextarea label="Descrizione (IT)" value={form.description?.it || ''} onChange={(v) => patchLocalized('description', 'it', v)} />
            <LocalizedTextarea label="Descrizione (EN)" value={form.description?.en || ''} onChange={(v) => patchLocalized('description', 'en', v)} />
          </div>
        </section>

        {/* Fields */}
        <section className="mt-6 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Campi del modulo</h3>
            <Button size="sm" variant="outline" onClick={addField} data-testid="admin-form-builder-add-field">
              <Plus className="mr-1 h-3 w-3" /> Aggiungi campo
            </Button>
          </div>

          {(form.fields || []).map((f, idx) => (
            <FieldEditor
              key={idx}
              field={f}
              index={idx}
              total={form.fields.length}
              onChange={(next) => setField(idx, next)}
              onMove={(dir) => moveField(idx, dir)}
              onRemove={() => removeField(idx)}
            />
          ))}
        </section>
      </div>
    </div>
  );
};

const LocalizedInput = ({ label, value, onChange, testId }) => (
  <div>
    <Label className="text-[12px]">{label}</Label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testId} />
  </div>
);

const LocalizedTextarea = ({ label, value, onChange }) => (
  <div>
    <Label className="text-[12px]">{label}</Label>
    <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} className="mt-1" />
  </div>
);

// ==================================================================
// Field editor
// ==================================================================

const FieldEditor = ({ field, index, total, onChange, onMove, onRemove }) => {
  const patch = (delta) => onChange({ ...field, ...delta });
  const patchLocalized = (key, lang, value) => onChange({ ...field, [key]: { ...(field[key] || {}), [lang]: value } });
  const patchValidation = (delta) => onChange({ ...field, validation: { ...(field.validation || {}), ...delta } });

  const isOptioned = field.type === 'select' || field.type === 'radio';

  const setOption = (i, next) => {
    const options = [...(field.options || [])];
    options[i] = next;
    patch({ options });
  };
  const addOption = () => patch({ options: [...(field.options || []), { value: `opt_${(field.options || []).length + 1}`, label: emptyLocalized() }] });
  const removeOption = (i) => patch({ options: (field.options || []).filter((_, j) => j !== i) });

  return (
    <div className="rounded-lg border border-white/8 bg-white/[0.02] p-3" data-testid={`admin-form-builder-field-${index}`}>
      {/* Field header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="rounded bg-amber-500/20 px-1.5 py-[1px] text-[10px] font-mono text-amber-200">#{index + 1}</span>
          <Select value={field.type} onValueChange={(v) => patch({ type: v })}>
            <SelectTrigger className="h-7 w-[160px] text-[11px]" data-testid={`admin-form-builder-field-${index}-type`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIELD_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" disabled={index === 0} onClick={() => onMove('up')} className="rounded p-1 text-muted-foreground hover:bg-white/10 disabled:opacity-30" aria-label="Sposta su">
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button type="button" disabled={index === total - 1} onClick={() => onMove('down')} className="rounded p-1 text-muted-foreground hover:bg-white/10 disabled:opacity-30" aria-label="Sposta giù">
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={onRemove} className="rounded p-1 text-red-300 hover:bg-red-500/10" aria-label="Elimina campo" data-testid={`admin-form-builder-field-${index}-remove`}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-[11px]">Chiave (field_key)</Label>
          <Input value={field.key} onChange={(e) => patch({ key: e.target.value.replace(/[^a-z0-9_]/gi, '').toLowerCase() })} className="mt-1 font-mono text-[12px]" />
        </div>
        <div className="flex items-end gap-2">
          <label className="mb-1.5 flex cursor-pointer items-center gap-2 text-[12px]">
            <input
              type="checkbox"
              checked={!!field.required}
              onChange={(e) => patch({ required: e.target.checked })}
              className="accent-amber-400"
              data-testid={`admin-form-builder-field-${index}-required`}
            />
            <span>Obbligatorio</span>
          </label>
        </div>
        <LocalizedInput label="Etichetta (IT)" value={field.label?.it || ''} onChange={(v) => patchLocalized('label', 'it', v)} />
        <LocalizedInput label="Etichetta (EN)" value={field.label?.en || ''} onChange={(v) => patchLocalized('label', 'en', v)} />
        {['text', 'textarea', 'email', 'phone', 'number', 'select'].includes(field.type) && (
          <>
            <LocalizedInput label="Placeholder (IT)" value={field.placeholder?.it || ''} onChange={(v) => patchLocalized('placeholder', 'it', v)} />
            <LocalizedInput label="Placeholder (EN)" value={field.placeholder?.en || ''} onChange={(v) => patchLocalized('placeholder', 'en', v)} />
          </>
        )}
        <LocalizedInput label="Aiuto (IT)" value={field.help_text?.it || ''} onChange={(v) => patchLocalized('help_text', 'it', v)} />
        <LocalizedInput label="Aiuto (EN)" value={field.help_text?.en || ''} onChange={(v) => patchLocalized('help_text', 'en', v)} />
      </div>

      {/* Length validation for strings */}
      {['text', 'textarea', 'email', 'phone'].includes(field.type) && (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px]">Min. caratteri</Label>
            <Input type="number" value={field.validation?.min || ''} onChange={(e) => patchValidation({ min: e.target.value ? Number(e.target.value) : undefined })} className="mt-1" />
          </div>
          <div>
            <Label className="text-[11px]">Max. caratteri</Label>
            <Input type="number" value={field.validation?.max || ''} onChange={(e) => patchValidation({ max: e.target.value ? Number(e.target.value) : undefined })} className="mt-1" />
          </div>
        </div>
      )}

      {/* Options for select/radio */}
      {isOptioned && (
        <div className="mt-3 rounded-md border border-dashed border-white/10 p-2">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Opzioni</p>
            <Button size="sm" variant="ghost" onClick={addOption} className="h-7 px-2 text-[11px]" data-testid={`admin-form-builder-field-${index}-add-option`}>
              <Plus className="mr-1 h-3 w-3" /> Opzione
            </Button>
          </div>
          <div className="space-y-1.5">
            {(field.options || []).map((opt, i) => (
              <div key={i} className="grid grid-cols-[100px_1fr_1fr_auto] items-center gap-2 rounded bg-white/[0.02] px-2 py-1.5">
                <Input value={opt.value} onChange={(e) => setOption(i, { ...opt, value: e.target.value.replace(/[^a-z0-9_]/gi, '').toLowerCase() })} placeholder="value" className="h-7 font-mono text-[11px]" />
                <Input value={opt.label?.it || ''} onChange={(e) => setOption(i, { ...opt, label: { ...(opt.label || {}), it: e.target.value } })} placeholder="Etichetta IT" className="h-7 text-[12px]" />
                <Input value={opt.label?.en || ''} onChange={(e) => setOption(i, { ...opt, label: { ...(opt.label || {}), en: e.target.value } })} placeholder="Label EN" className="h-7 text-[12px]" />
                <button type="button" onClick={() => removeOption(i)} className="rounded p-1 text-red-300 hover:bg-red-500/10" aria-label="Elimina opzione">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {(field.options || []).length === 0 && (
              <p className="p-2 text-[11px] text-muted-foreground">Nessuna opzione. Aggiungi almeno una voce.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFormBuilder;
